import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Typography, Grid, IconButton,
  Alert, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Divider, Tabs, Tab, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField
} from '@mui/material';
import {
  ArrowBack as BackIcon, Edit as EditIcon, PlayArrow as StartIcon,
  CheckCircle as CompleteIcon, Cancel as CancelIcon, Pause as HoldIcon,
  Send as SubmitIcon, ThumbUp as ApproveIcon, Refresh as ResumeIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi, { MillingOrder, OrderStatus } from '../../services/millingApi';

const statusColors: Record<OrderStatus, 'default' | 'warning' | 'info' | 'primary' | 'success' | 'error'> = {
  draft: 'default',
  pending_approval: 'warning',
  approved: 'info',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'error',
  on_hold: 'warning',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const MillingOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<MillingOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [actionDialog, setActionDialog] = useState<{ open: boolean; action: string; reason: string }>({
    open: false,
    action: '',
    reason: '',
  });

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const data = await millingApi.getOrder(id!);
      setOrder(data);
    } catch (err) {
      console.error(err);
      setError('Error loading order');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: string) => {
    if (!order) return;
    try {
      switch (action) {
        case 'submit':
          await millingApi.submitForApproval(order.id!, { updatedBy: 'admin' });
          break;
        case 'approve':
          await millingApi.approveOrder(order.id!, { approvedBy: 'admin' });
          break;
        case 'start':
          await millingApi.startOrder(order.id!, { updatedBy: 'admin' });
          break;
        case 'complete':
          await millingApi.completeOrder(order.id!, { completedBy: 'admin' });
          break;
        case 'cancel':
          await millingApi.cancelOrder(order.id!, { cancelledBy: 'admin', reason: actionDialog.reason });
          break;
        case 'hold':
          await millingApi.holdOrder(order.id!, { updatedBy: 'admin', reason: actionDialog.reason });
          break;
        case 'resume':
          await millingApi.resumeOrder(order.id!, { updatedBy: 'admin' });
          break;
      }
      setActionDialog({ open: false, action: '', reason: '' });
      fetchOrder();
    } catch (err) {
      alert(`Error performing action: ${action}`);
    }
  };

  const openActionDialog = (action: string) => {
    if (['cancel', 'hold'].includes(action)) {
      setActionDialog({ open: true, action, reason: '' });
    } else {
      handleAction(action);
    }
  };

  const getAvailableActions = (status: OrderStatus) => {
    switch (status) {
      case 'draft': return ['submit'];
      case 'pending_approval': return ['approve', 'cancel'];
      case 'approved': return ['start', 'cancel'];
      case 'in_progress': return ['complete', 'hold', 'cancel'];
      case 'on_hold': return ['resume', 'cancel'];
      default: return [];
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error || !order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Order not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/milling/orders')}>Back to List</Button>
      </Box>
    );
  }

  const availableActions = getAvailableActions(order.status);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/milling/orders')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">{order.orderNumber}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {order.recipeName} | {new Date(order.orderDate).toLocaleDateString()}
            </Typography>
          </Box>
          <Chip label={order.status.replace('_', ' ')} color={statusColors[order.status]} sx={{ ml: 2 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {availableActions.includes('submit') && (
            <Button startIcon={<SubmitIcon />} variant="outlined" onClick={() => openActionDialog('submit')}>
              Submit
            </Button>
          )}
          {availableActions.includes('approve') && (
            <Button startIcon={<ApproveIcon />} color="success" variant="contained" onClick={() => openActionDialog('approve')}>
              Approve
            </Button>
          )}
          {availableActions.includes('start') && (
            <Button startIcon={<StartIcon />} color="primary" variant="contained" onClick={() => openActionDialog('start')}>
              Start
            </Button>
          )}
          {availableActions.includes('complete') && (
            <Button startIcon={<CompleteIcon />} color="success" variant="contained" onClick={() => openActionDialog('complete')}>
              Complete
            </Button>
          )}
          {availableActions.includes('resume') && (
            <Button startIcon={<ResumeIcon />} color="primary" variant="contained" onClick={() => openActionDialog('resume')}>
              Resume
            </Button>
          )}
          {availableActions.includes('hold') && (
            <Button startIcon={<HoldIcon />} color="warning" variant="outlined" onClick={() => openActionDialog('hold')}>
              Hold
            </Button>
          )}
          {availableActions.includes('cancel') && (
            <Button startIcon={<CancelIcon />} color="error" variant="outlined" onClick={() => openActionDialog('cancel')}>
              Cancel
            </Button>
          )}
          {order.status === 'draft' && (
            <Button startIcon={<EditIcon />} variant="contained" onClick={() => navigate(`/milling/orders/${id}/edit`)}>
              Edit
            </Button>
          )}
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Inputs" />
        <Tab label="Outputs" />
        <Tab label="Costs" />
        <Tab label="Variance" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Planned Quantities</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Input Qty</Typography>
                    <Typography fontWeight="bold">{order.plannedInputQty}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Output Qty</Typography>
                    <Typography fontWeight="bold">{order.plannedOutputQty}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Expected Yield</Typography>
                    <Typography fontWeight="bold" color="primary">{order.plannedYieldPercent}%</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Actual Quantities</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Input Qty</Typography>
                    <Typography fontWeight="bold">{order.actualInputQty || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Output Qty</Typography>
                    <Typography fontWeight="bold">{order.actualOutputQty || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Actual Yield</Typography>
                    <Typography fontWeight="bold" color={order.actualYieldPercent && order.actualYieldPercent >= (order.plannedYieldPercent || 0) ? 'success.main' : 'error.main'}>
                      {order.actualYieldPercent ? `${order.actualYieldPercent}%` : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Yield Variance</Typography>
                <Typography variant="h3" color={order.yieldVariance && order.yieldVariance >= 0 ? 'success.main' : 'error.main'}>
                  {order.yieldVariance !== undefined ? `${order.yieldVariance >= 0 ? '+' : ''}${order.yieldVariance.toFixed(2)}%` : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {order.yieldVariance !== undefined && order.yieldVariance >= 0 ? 'Above target' : 'Below target'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Schedule</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Planned Start</Typography>
                    <Typography>{order.plannedStartDate ? new Date(order.plannedStartDate).toLocaleDateString() : '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Planned End</Typography>
                    <Typography>{order.plannedEndDate ? new Date(order.plannedEndDate).toLocaleDateString() : '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Actual Start</Typography>
                    <Typography>{order.actualStartDate ? new Date(order.actualStartDate).toLocaleString() : '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Actual End</Typography>
                    <Typography>{order.actualEndDate ? new Date(order.actualEndDate).toLocaleString() : '-'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Cost Summary</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Planned Total Cost</Typography>
                    <Typography fontWeight="bold">${order.plannedTotalCost?.toFixed(2) || '0.00'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Actual Total Cost</Typography>
                    <Typography fontWeight="bold">${order.actualTotalCost?.toFixed(2) || '0.00'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Cost Variance</Typography>
                    <Typography color={order.costVariance && order.costVariance <= 0 ? 'success.main' : 'error.main'}>
                      {order.costVariance ? `$${order.costVariance.toFixed(2)}` : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Unit Cost</Typography>
                    <Typography fontWeight="bold">${order.unitCost?.toFixed(2) || '0.00'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {order.notes && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Notes</Typography>
                  <Typography>{order.notes}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Input Materials</Typography>
            {order.inputRows && order.inputRows.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell align="right">Planned Qty</TableCell>
                      <TableCell align="right">Actual Qty</TableCell>
                      <TableCell align="right">Variance</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                      <TableCell align="right">Total Cost</TableCell>
                      <TableCell align="center">Issued</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.inputRows.map((row, idx) => (
                      <TableRow key={row.id || idx}>
                        <TableCell>{row.lineNo}</TableCell>
                        <TableCell><Typography fontWeight="bold">{row.itemCode}</Typography></TableCell>
                        <TableCell>{row.itemName}</TableCell>
                        <TableCell>{row.uom}</TableCell>
                        <TableCell align="right">{row.plannedQty}</TableCell>
                        <TableCell align="right">{row.actualQty || '-'}</TableCell>
                        <TableCell align="right">
                          {row.varianceQty !== undefined ? (
                            <Typography color={row.varianceQty <= 0 ? 'success.main' : 'error.main'}>
                              {row.varianceQty}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">${row.unitCost?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell align="right">${row.totalCost?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell align="center">
                          <Chip label={row.isIssued ? 'Yes' : 'No'} size="small" color={row.isIssued ? 'success' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">No input materials recorded.</Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Output Products</Typography>
            {order.outputRows && order.outputRows.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Planned Qty</TableCell>
                      <TableCell align="right">Actual Qty</TableCell>
                      <TableCell align="right">Yield %</TableCell>
                      <TableCell align="right">Allocated Cost</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                      <TableCell align="center">Received</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.outputRows.map((row, idx) => (
                      <TableRow key={row.id || idx}>
                        <TableCell>{row.lineNo}</TableCell>
                        <TableCell><Typography fontWeight="bold">{row.itemCode}</Typography></TableCell>
                        <TableCell>{row.itemName}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.outputType.replace('_', ' ')}
                            size="small"
                            color={row.isMainProduct ? 'primary' : row.isByProduct ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">{row.plannedQty}</TableCell>
                        <TableCell align="right">{row.actualQty || '-'}</TableCell>
                        <TableCell align="right">{row.yieldPercent?.toFixed(1) || '-'}%</TableCell>
                        <TableCell align="right">${row.allocatedCost?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell align="right">${row.unitCost?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell align="center">
                          <Chip label={row.isReceived ? 'Yes' : 'No'} size="small" color={row.isReceived ? 'success' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">No output products recorded.</Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Cost Breakdown</Typography>
                {order.costRows && order.costRows.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Cost Type</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Planned</TableCell>
                          <TableCell align="right">Actual</TableCell>
                          <TableCell align="right">Variance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order.costRows.map((row, idx) => (
                          <TableRow key={row.id || idx}>
                            <TableCell>
                              <Chip label={row.costType} size="small" />
                            </TableCell>
                            <TableCell>{row.costDescription || '-'}</TableCell>
                            <TableCell align="right">${row.plannedAmount?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell align="right">${row.actualAmount?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell align="right">
                              <Typography color={row.varianceAmount && row.varianceAmount <= 0 ? 'success.main' : 'error.main'}>
                                ${row.varianceAmount?.toFixed(2) || '0.00'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={2}><Typography fontWeight="bold">Total</Typography></TableCell>
                          <TableCell align="right"><Typography fontWeight="bold">${order.plannedTotalCost?.toFixed(2) || '0.00'}</Typography></TableCell>
                          <TableCell align="right"><Typography fontWeight="bold">${order.actualTotalCost?.toFixed(2) || '0.00'}</Typography></TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color={order.costVariance && order.costVariance <= 0 ? 'success.main' : 'error.main'}>
                              ${order.costVariance?.toFixed(2) || '0.00'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">No costs recorded.</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Cost by Type</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Labor</TableCell>
                      <TableCell align="right">${order.actualLaborCost?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Material</TableCell>
                      <TableCell align="right">${order.actualMaterialCost?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Overhead</TableCell>
                      <TableCell align="right">${order.actualOverheadCost?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Depreciation</TableCell>
                      <TableCell align="right">${order.actualDepreciationCost?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Utilities</TableCell>
                      <TableCell align="right">${order.actualUtilitiesCost?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Other</TableCell>
                      <TableCell align="right">${order.actualOtherCost?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Quantity Variance</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Input Variance</TableCell>
                      <TableCell align="right">
                        {(order.actualInputQty || 0) - (order.plannedInputQty || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Output Variance</TableCell>
                      <TableCell align="right">
                        {(order.actualOutputQty || 0) - (order.plannedOutputQty || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography fontWeight="bold">Yield Variance</Typography></TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color={order.yieldVariance && order.yieldVariance >= 0 ? 'success.main' : 'error.main'}>
                          {order.yieldVariance?.toFixed(2) || '0'}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Cost Variance</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Planned Cost</TableCell>
                      <TableCell align="right">${order.plannedTotalCost?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Actual Cost</TableCell>
                      <TableCell align="right">${order.actualTotalCost?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography fontWeight="bold">Variance</Typography></TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color={order.costVariance && order.costVariance <= 0 ? 'success.main' : 'error.main'}>
                          ${order.costVariance?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Recipe</Typography>
            <Typography>{order.recipeCode}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Machine</Typography>
            <Typography>{order.machineName || '-'}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Operator</Typography>
            <Typography>{order.operatorName || '-'}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Created By</Typography>
            <Typography>{order.createdBy}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Created At</Typography>
            <Typography>{order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Updated At</Typography>
            <Typography>{order.updatedAt ? new Date(order.updatedAt).toLocaleString() : '-'}</Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Action Dialog for Cancel/Hold */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, action: '', reason: '' })}>
        <DialogTitle>
          {actionDialog.action === 'cancel' ? 'Cancel Order' : 'Put Order on Hold'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason"
            value={actionDialog.reason}
            onChange={(e) => setActionDialog((prev) => ({ ...prev, reason: e.target.value }))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, action: '', reason: '' })}>Cancel</Button>
          <Button
            variant="contained"
            color={actionDialog.action === 'cancel' ? 'error' : 'warning'}
            onClick={() => handleAction(actionDialog.action)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MillingOrderDetail;
